/**
 * useFeatureFlags — Every conditional render in the platform uses this.
 * (v4.8 Spec — Section 4, Phase 1 Mandate item 3)
 *
 * Three resolution levels:
 *   1. System-wide: off for everyone in the organisation
 *   2. Role-level: off for specific roles
 *   3. User-level: off for specific users
 *
 * Dependency-aware: if a parent feature is disabled, all children are too.
 * Cached with 5-minute TTL. Checked on every page render.
 *
 * Components use: const { isEnabled } = useFeatureFlags();
 *   if (!isEnabled('ai_assistant_chat')) return null;
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuditLog } from './useAuditLog';
import { createServiceError, fromSupabaseError } from '@/lib/errors';
import type { ServiceResult, UUID, UserRole } from '@/types';
import type { FeatureFlag } from '@/types/database';

/** Cache TTL in milliseconds — 5 minutes per v4.8 spec */
const CACHE_TTL_MS = 5 * 60 * 1000;

/** Cached flag data */
interface FlagCache {
  readonly flags: FeatureFlag[];
  readonly timestamp: number;
}

/** Return type for useFeatureFlags hook */
interface UseFeatureFlagsReturn {
  /** Check if a feature is enabled for the current user */
  readonly isEnabled: (featureKey: string) => boolean;
  /** All flags for the current organisation (for admin UI) */
  readonly flags: FeatureFlag[];
  /** Loading state */
  readonly loading: boolean;
  /** Force refresh the cache */
  readonly refresh: () => Promise<void>;
  /** Toggle a flag (Director only — writes audit log) */
  readonly toggleFlag: (
    featureKey: string,
    enabled: boolean
  ) => Promise<ServiceResult<FeatureFlag>>;
}

/** Module-level cache shared across all hook instances */
let flagCache: FlagCache | null = null;

export function useFeatureFlags(): UseFeatureFlagsReturn {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const { log: auditLog } = useAuditLog();
  const userContextRef = useRef<{
    userId: string;
    role: UserRole;
    organisationId: UUID;
  } | null>(null);

  /** Fetch all flags for the current user's organisation */
  const fetchFlags = useCallback(async (): Promise<void> => {
    // Check cache freshness
    if (flagCache && Date.now() - flagCache.timestamp < CACHE_TTL_MS) {
      setFlags(flagCache.flags);
      setLoading(false);
      return;
    }

    try {
      // Get current user context
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      // Get user profile for role and org
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organisation_id, role')
        .eq('id', user.id)
        .single();

      if (!profile) {
        setLoading(false);
        return;
      }

      userContextRef.current = {
        userId: user.id,
        role: profile.role as UserRole,
        organisationId: profile.organisation_id as UUID,
      };

      // Fetch all flags for this organisation (including global flags)
      const { data: flagData, error } = await supabase
        .from('feature_flags')
        .select('*')
        .or(`organisation_id.eq.${profile.organisation_id},organisation_id.is.null`);

      if (error) {
        console.error('Failed to fetch feature flags:', error.message);
        setLoading(false);
        return;
      }

      const fetchedFlags = (flagData ?? []) as FeatureFlag[];

      // Update cache
      flagCache = {
        flags: fetchedFlags,
        timestamp: Date.now(),
      };

      setFlags(fetchedFlags);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchFlags();
  }, [fetchFlags]);

  /**
   * Resolve whether a feature is enabled for the current user.
   *
   * Resolution order (most specific wins):
   *   1. User-level override (scope=user, scope_value=userId)
   *   2. Role-level override (scope=role, scope_value=userRole)
   *   3. System-level flag (scope=system)
   *
   * Dependency check: if parent_feature_key is set and parent is disabled,
   * this feature is also disabled regardless of its own flag state.
   */
  const isEnabled = useCallback(
    (featureKey: string): boolean => {
      if (loading || flags.length === 0) {
        // While loading, features are disabled by default (safe default)
        return false;
      }

      const context = userContextRef.current;
      if (!context) return false;

      return resolveFlag(featureKey, flags, context.userId, context.role);
    },
    [flags, loading]
  );

  /** Force refresh — bypasses cache */
  const refresh = useCallback(async (): Promise<void> => {
    flagCache = null;
    setLoading(true);
    await fetchFlags();
  }, [fetchFlags]);

  /** Toggle a feature flag. Director only (RLS enforced). Writes audit log. */
  const toggleFlag = useCallback(
    async (
      featureKey: string,
      enabled: boolean
    ): Promise<ServiceResult<FeatureFlag>> => {
      const context = userContextRef.current;
      if (!context) {
        return {
          data: null,
          error: createServiceError('PERMISSION_DENIED', 'User context not loaded'),
          loading: false,
        };
      }

      // Find the existing system-level flag
      const existingFlag = flags.find(
        (f) => f.feature_key === featureKey && f.scope === 'system'
      );

      if (!existingFlag) {
        return {
          data: null,
          error: createServiceError('NOT_FOUND', `Feature flag '${featureKey}' not found`),
          loading: false,
        };
      }

      try {
        const { data, error } = await supabase
          .from('feature_flags')
          .update({
            is_enabled: enabled,
            changed_by: context.userId,
            changed_at: new Date().toISOString(),
          })
          .eq('id', existingFlag.id)
          .select()
          .single();

        if (error) {
          return {
            data: null,
            error: fromSupabaseError(error),
            loading: false,
          };
        }

        // Audit log — every flag change is recorded (v4.8 Spec Section 4.1)
        await auditLog({
          organisationId: context.organisationId,
          action: 'update',
          entityType: 'feature_flag',
          entityId: existingFlag.id,
          beforeValue: { is_enabled: existingFlag.is_enabled },
          afterValue: { is_enabled: enabled },
          metadata: { feature_key: featureKey },
        });

        // Invalidate cache and refresh
        flagCache = null;
        await fetchFlags();

        return { data: data as FeatureFlag, error: null, loading: false };
      } catch (err) {
        return {
          data: null,
          error: createServiceError(
            'UNKNOWN_ERROR',
            err instanceof Error ? err.message : 'Failed to toggle feature flag',
            { retry: true }
          ),
          loading: false,
        };
      }
    },
    [flags, auditLog, fetchFlags]
  );

  return { isEnabled, flags, loading, refresh, toggleFlag };
}

// ─────────────────────────────────────────────────────────────────────────────
// Flag resolution logic (pure function — testable)
// ─────────────────────────────────────────────────────────────────────────────

function resolveFlag(
  featureKey: string,
  allFlags: FeatureFlag[],
  userId: string,
  userRole: UserRole
): boolean {
  // 1. Check parent dependency first
  const flagsForKey = allFlags.filter((f) => f.feature_key === featureKey);
  const systemFlag = flagsForKey.find((f) => f.scope === 'system');

  if (systemFlag?.parent_feature_key) {
    const parentEnabled = resolveFlag(
      systemFlag.parent_feature_key,
      allFlags,
      userId,
      userRole
    );
    if (!parentEnabled) return false;
  }

  // 2. User-level override (most specific)
  const userFlag = flagsForKey.find(
    (f) => f.scope === 'user' && f.scope_value === userId
  );
  if (userFlag) return userFlag.is_enabled;

  // 3. Role-level override
  const roleFlag = flagsForKey.find(
    (f) => f.scope === 'role' && f.scope_value === userRole
  );
  if (roleFlag) return roleFlag.is_enabled;

  // 4. System-level flag (default)
  if (systemFlag) return systemFlag.is_enabled;

  // 5. Unknown feature — disabled by default (safe)
  return false;
}
